import {
	AddTagButton,
	Container,
	CreateFormContainer,
	EmptyStateInline,
	EmptyText,
	FormActions,
	FormButton,
	FormField,
	FormLabel,
	InlineSelectorContainer,
	Input,
	Label,
	RemoveButton,
	SearchContainer,
	SearchIcon,
	SearchInput,
	SelectorHeader,
	SelectorTitle,
	TagChipContainer,
	TagColorPreview,
	TagContent,
	TagDescription,
	TagInfo,
	TagItem,
	TagItemDescription,
	TagItemName,
	TagName,
	TagsList,
	TagsWrapper,
	TextArea,
} from './TicketTagsField.styles';
import { CreateTagRequest, TagType } from 'features/Dashboard/components/TagsSection/types';
import { FC, useMemo, useState } from 'react';
import {
	addTagsToTicketType,
	createTag,
	removeTagsFromTicketType,
} from 'features/Dashboard/components/TagsSection/services';

import { NEW_TICKET } from '../../utils';
import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';
import { TYPES } from 'hooks/useToastNotification/constants';
import { Tag } from 'features/common/types';
import { useLocation } from 'react-router-dom';
import { useTagsApi } from 'features/Dashboard/components/TagsSection/hooks/useTagsApi';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';

interface TicketTagsFieldProps {
	ticketTags?: Tag[];
	ticketTypeId?: number;
}

const MAX_TAGS = 10;

const TicketTagsField: FC<TicketTagsFieldProps> = ({ ticketTags = [], ticketTypeId }) => {
	const { t } = useTranslation();
	const location = useLocation();
	const { showNotification } = useToastNotification();
	const { tags: availableTags, refetch: refetchTags } = useTagsApi();

	const [showSelector, setShowSelector] = useState(false);
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [newTagName, setNewTagName] = useState('');
	const [newTagDescription, setNewTagDescription] = useState('');
	const [newTagColor, setNewTagColor] = useState('#F5F5F5');

	const isNewTicket = location.pathname.includes(NEW_TICKET);

	// Normalize tags to ensure they have lowercase 'id' property
	const normalizedTags = ticketTags.map((tag) => ({
		...tag,
		id: tag.id || (tag as any).ID,
	}));

	const selectedTagIds = normalizedTags.map((tag) => tag.id);
	const canAddMore = selectedTagIds.length < MAX_TAGS;

	const filteredTags = useMemo(() => {
		if (!Array.isArray(availableTags)) return [];
		return availableTags
			.filter((tag) => tag.tag_type === TagType.TICKET_TYPE)
			.filter((tag) => tag.name.toLowerCase().includes(searchQuery.toLowerCase()))
			.map((tag) => ({
				...tag,
				id: tag.id || (tag as any).ID,
			}));
	}, [availableTags, searchQuery]);

	const handleAddTag = async (tag: Tag) => {
		if (!ticketTypeId || isNewTicket) {
			showNotification({
				message: t('tags-ticket-save-first'),
				type: TYPES.INFO,
			});
			return;
		}

		if (!canAddMore) {
			showNotification({ message: t('tags-max-limit-reached'), type: TYPES.INFO });
			return;
		}

		try {
			await addTagsToTicketType(ticketTypeId, { tag_ids: [tag.id] });
			showNotification({ message: t('tags-added-successfully'), type: TYPES.SUCCESS });
			window.location.reload();
		} catch (error) {
			showNotification({ message: t('tags-add-error'), type: TYPES.ERROR });
		}
	};

	const handleRemoveTag = async (tagId: number) => {
		if (!ticketTypeId || isNewTicket) {
			return;
		}

		try {
			await removeTagsFromTicketType(ticketTypeId, { tag_ids: [tagId] });
			showNotification({ message: t('tags-removed-successfully'), type: TYPES.SUCCESS });
			window.location.reload();
		} catch (error) {
			showNotification({ message: t('tags-remove-error'), type: TYPES.ERROR });
		}
	};

	const handleCreateTag = async () => {
		if (!ticketTypeId || isNewTicket) {
			showNotification({
				message: t('tags-ticket-save-first'),
				type: TYPES.INFO,
			});
			return;
		}

		if (!newTagName.trim()) {
			showNotification({ message: t('tags-name-required'), type: TYPES.ERROR });
			return;
		}

		try {
			const tagData: CreateTagRequest = {
				name: newTagName.trim(),
				description: newTagDescription.trim() || undefined,
				color: newTagColor,
				tag_type: TagType.TICKET_TYPE,
			};

			const newTag = await createTag(tagData);
			await refetchTags();
			showNotification({ message: t('tags-created-successfully'), type: TYPES.SUCCESS });

			await addTagsToTicketType(ticketTypeId, { tag_ids: [newTag.id] });

			// Reset form
			setNewTagName('');
			setNewTagDescription('');
			setNewTagColor('#F5F5F5');
			setShowCreateForm(false);

			window.location.reload();
		} catch (error) {
			showNotification({ message: t('tags-create-error'), type: TYPES.ERROR });
		}
	};

	const handleOpenSelector = () => {
		if (isNewTicket) {
			showNotification({
				message: t('tags-ticket-save-first'),
				type: TYPES.INFO,
			});
			return;
		}

		if (canAddMore) {
			setShowSelector(true);
			setSearchQuery('');
		}
	};

	const handleCloseSelector = () => {
		setShowSelector(false);
		setShowCreateForm(false);
		setSearchQuery('');
		setNewTagName('');
		setNewTagDescription('');
		setNewTagColor('#F5F5F5');
	};

	const handleToggleCreateForm = () => {
		setShowCreateForm(!showCreateForm);
		if (showCreateForm) {
			// Reset form when closing
			setNewTagName('');
			setNewTagDescription('');
			setNewTagColor('#F5F5F5');
		}
	};

	return (
		<Container>
			<Label>
				{t('tags-ticket-tags')} ({normalizedTags.length}/{MAX_TAGS}) - {t('optional')}
			</Label>

			<TagsWrapper>
				{normalizedTags.length > 0 ? (
					<>
						{normalizedTags.map((tag) => (
							<TagChipContainer key={tag.id} $color={''}>
								<TagContent>
									<TagName>{tag.name}</TagName>
									{tag.description && <TagDescription>{tag.description}</TagDescription>}
								</TagContent>
								{!isNewTicket && (
									<RemoveButton
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											handleRemoveTag(tag.id);
										}}
										aria-label={`Remove ${tag.name}`}
									>
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
											<path d="M18 6L6 18M6 6l12 12" />
										</svg>
									</RemoveButton>
								)}
							</TagChipContainer>
						))}
						{!isNewTicket && canAddMore && (
							<AddTagButton type="button" onClick={handleOpenSelector}>
								+ {t('tags-add')}
							</AddTagButton>
						)}
					</>
				) : (
					<>
						<EmptyText>{isNewTicket ? t('tags-save-ticket-first') : t('tags-no-tags-assigned')}</EmptyText>
						{!isNewTicket && (
							<AddTagButton type="button" onClick={handleOpenSelector}>
								+ {t('tags-add')}
							</AddTagButton>
						)}
					</>
				)}
			</TagsWrapper>

			{showSelector && (
				<InlineSelectorContainer>
					<SelectorHeader>
						<SelectorTitle>{t('tags-select-tags')}</SelectorTitle>
					</SelectorHeader>

					<SearchContainer>
						<SearchIcon>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<circle cx="11" cy="11" r="8" />
								<path d="M21 21l-4.35-4.35" />
							</svg>
						</SearchIcon>
						<SearchInput
							type="text"
							placeholder={t('tags-search-placeholder')}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</SearchContainer>

					{filteredTags.length > 0 ? (
						<>
							<TagsList>
								{filteredTags.map((tag) => {
									const isSelected = selectedTagIds.includes(tag.id);
									const isDisabled = !isSelected && !canAddMore;

									return (
										<TagItem
											key={tag.id}
											$selected={isSelected}
											onClick={() => !isDisabled && handleAddTag(tag)}
											style={{
												opacity: isDisabled ? 0.5 : 1,
												cursor: isDisabled ? 'not-allowed' : 'pointer',
											}}
										>
											<TagColorPreview $color={tag.color || ''} />
											<TagInfo>
												<TagItemName>{tag.name}</TagItemName>
												{tag.description && <TagItemDescription>{tag.description}</TagItemDescription>}
											</TagInfo>
											{isSelected && <span>✓</span>}
										</TagItem>
									);
								})}
							</TagsList>
							{!showCreateForm && (
								<FormActions>
									<PunchupButton
										type="button"
										variant="secondary"
										text={t('button-cancel')}
										onClick={handleCloseSelector}
										style={{ flex: 1 }}
									/>
									<PunchupButton
										type="button"
										variant="primary"
										text={t('button-create-new')}
										style={{ flex: 1 }}
										onClick={handleToggleCreateForm}
									/>
								</FormActions>
							)}
						</>
					) : (
						<>
							<EmptyStateInline>{t('tags-no-tags-found')}</EmptyStateInline>
							<FormActions>
								<PunchupButton
									type="button"
									variant="secondary"
									text={t('button-cancel')}
									onClick={handleCloseSelector}
									style={{ flex: 1 }}
								/>
								<PunchupButton
									type="button"
									variant="primary"
									text={t('button-create-new')}
									onClick={handleToggleCreateForm}
									style={{ flex: 1 }}
								/>
							</FormActions>
						</>
					)}

					{showCreateForm && (
						<CreateFormContainer>
							<FormField>
								<FormLabel>
									{t('tags-name')} <span style={{ color: 'red' }}>*</span>
								</FormLabel>
								<Input
									type="text"
									placeholder={t('tags-name-placeholder')}
									value={newTagName}
									onChange={(e) => setNewTagName(e.target.value)}
									maxLength={50}
								/>
							</FormField>

							<FormField>
								<FormLabel>{t('tags-description')}</FormLabel>
								<TextArea
									placeholder={t('tags-description-placeholder')}
									value={newTagDescription}
									onChange={(e) => setNewTagDescription(e.target.value)}
									maxLength={200}
								/>
							</FormField>

							<FormActions>
								<FormButton type="button" $variant="secondary" onClick={handleToggleCreateForm}>
									{t('cancel')}
								</FormButton>
								<FormButton type="button" $variant="primary" onClick={handleCreateTag} disabled={!newTagName.trim()}>
									{t('tags-create-add')}
								</FormButton>
							</FormActions>
						</CreateFormContainer>
					)}
				</InlineSelectorContainer>
			)}
		</Container>
	);
};

export { TicketTagsField };
